import React, { useCallback, useEffect, useMemo, useState } from 'react'
import moment from 'moment'

import Card from 'react-bootstrap/Card'
import Stack from 'react-bootstrap/Stack'
import Button from 'react-bootstrap/Button'
import Modal from 'react-bootstrap/Modal'
import Table from 'react-bootstrap/Table'
import Form from 'react-bootstrap/Form'

import { constants } from '@blocktickets/shared-utils'

import {
  capitalizeString,
  formatCurrency,
  formatDateTime,
  formatPhoneNumber,
  formatString,
} from '../../utilities/helpers'

import { getDepositsForOrganization } from '../../utilities/api'
import { useReportRefresh } from '../../hooks/useReportRefresh'

import { useDepositModal } from '../../providers/DepositProvider/DepositProvider'

import { EmptyContainer } from '../EmptyContainer'
import { PageLoadingContainer } from '../PageLoadingContainer'
import { ReportActions } from '../ReportActions'

const { PAYMENT_TYPE, PAYMENT_TYPE_LABEL } = constants

function formatSeatInfoLines(seatInfo) {
  if (!Array.isArray(seatInfo) || seatInfo.length === 0) return '—'
  return (
    seatInfo
      .map((entry) => {
        const attrs = entry?.attributes ?? entry
        const parts = [
          attrs?.section,
          attrs?.row,
          attrs?.seats ?? attrs?.seat,
        ].filter((p) => p != null && String(p).trim() !== '')
        return parts.length ? parts.join(' · ') : null
      })
      .filter(Boolean)
      .join('; ') || '—'
  )
}

function formatRecordedAt(row) {
  const d = row.attributes ?? row
  const created = row.createdAt ?? d.createdAt
  if (created) {
    return formatDateTime(moment(created))
  }
  if (d?.date) {
    const timePart =
      d?.time != null && String(d.time).trim() !== ''
        ? ` ${String(d.time).trim()}`
        : ''
    return `${d.date}${timePart}`.trim()
  }
  return '—'
}

function shortenId(id) {
  if (!id || typeof id !== 'string') return '—'
  if (id.length <= 14) return id
  return `${id.slice(0, 10)}…`
}

function depositNoteText(row) {
  const d = row.attributes ?? row
  const raw = d.noteText
  if (raw == null) return ''
  const s = String(raw).trim()
  return s
}

function DepositNoteIcon({ className }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
      width="18"
      height="18"
    >
      <path
        d="M8 4h8a2 2 0 012 2v11l-4-2.5L10 17.5V20H8a2 2 0 01-2-2V6a2 2 0 012-2z"
        stroke="currentColor"
        strokeWidth="1.35"
        strokeLinejoin="round"
      />
      <path
        d="M10 9h4M10 12h4"
        stroke="currentColor"
        strokeWidth="1.35"
        strokeLinecap="round"
      />
    </svg>
  )
}

/** Total charged (`amount`) minus sales tax and deposit service fee — same as deposit base plus processing fee. */
function depositListAmount(row) {
  const d = row.attributes ?? row
  const gross = Number(d.amount)
  const salesTax = Number(d.salesTax ?? 0) || 0
  const serviceFee = Number(d.serviceFee ?? 0) || 0
  const processingFee = Number(d.estimatedProcessingFee ?? 0) || 0
  if (!Number.isFinite(gross)) return 0
  return Math.round((gross - salesTax - serviceFee - processingFee) * 100) / 100
}

/** Prefer createdAt; fall back to deposit date + time fields. */
function getDepositRecordMoment(row) {
  const d = row.attributes ?? row
  const created = row.createdAt ?? d.createdAt
  if (created) {
    return moment(created)
  }
  if (d?.date) {
    const timePart =
      d?.time != null && String(d.time).trim() !== ''
        ? String(d.time).trim()
        : '00:00:00'
    const combined = moment(
      `${d.date} ${timePart}`,
      'YYYY-MM-DD HH:mm:ss',
      true
    )
    if (combined.isValid()) return combined
    return moment(d.date, 'YYYY-MM-DD', true)
  }
  return null
}

function depositMatchesFilters(row, fromDate, toDate, paymentMethodFilter) {
  if (paymentMethodFilter) {
    const d = row.attributes ?? row
    const pm = d.paymentMethod != null ? String(d.paymentMethod).trim() : ''
    if (pm !== paymentMethodFilter) return false
  }
  if (fromDate && toDate) {
    const m = getDepositRecordMoment(row)
    if (!m || !m.isValid()) return false
    const start = moment(fromDate).startOf('day')
    const end = moment(toDate).endOf('day')
    return m.valueOf() >= start.valueOf() && m.valueOf() <= end.valueOf()
  }
  return true
}

function DepositsWrapper({ organization }) {
  const { handleDepositShow, depositsRefreshTrigger } = useDepositModal()

  const [loading, setLoading] = useState(true)
  const { refreshToken, refresh, completeRefresh } = useReportRefresh(loading)
  const [deposits, setDeposits] = useState([])
  const [loadError, setLoadError] = useState(null)

  const [fromDate, setFromDate] = useState(null)
  const [toDate, setToDate] = useState(null)
  const [paymentMethodFilter, setPaymentMethodFilter] = useState('')
  const [depositNoteModal, setDepositNoteModal] = useState(null)

  const paymentMethodChoices = useMemo(() => {
    const baseValues = [PAYMENT_TYPE.CARD, PAYMENT_TYPE.CASH]
    const map = new Map()
    baseValues.forEach((v) => {
      map.set(v, PAYMENT_TYPE_LABEL[v] || formatString(v))
    })
    deposits.forEach((row) => {
      const d = row.attributes ?? row
      const v = d.paymentMethod != null ? String(d.paymentMethod).trim() : ''
      if (v && !map.has(v)) {
        map.set(v, PAYMENT_TYPE_LABEL[v] || formatString(v))
      }
    })
    return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1]))
  }, [deposits])

  const filteredDeposits = useMemo(
    () =>
      deposits.filter((row) =>
        depositMatchesFilters(row, fromDate, toDate, paymentMethodFilter)
      ),
    [deposits, fromDate, toDate, paymentMethodFilter]
  )

  const loadDeposits = useCallback(() => {
    if (!organization?.id) {
      setDeposits([])
      setLoading(false)
      return undefined
    }
    setLoadError(null)
    setLoading(true)
    return getDepositsForOrganization(organization.id)
      .then((res) => {
        const list = res?.data?.data
        setDeposits(Array.isArray(list) ? list : [])
      })
      .catch((err) => {
        console.error(err)
        setLoadError(
          err?.response?.data?.error?.message ||
            'Could not load deposits. Check your connection and permissions.'
        )
        setDeposits([])
      })
      .finally(() => {
        setLoading(false)
        completeRefresh()
      })
  }, [organization?.id])

  useEffect(() => {
    loadDeposits()
  }, [loadDeposits, depositsRefreshTrigger, refreshToken])

  useEffect(() => {
    setFromDate(null)
    setToDate(null)
    setPaymentMethodFilter('')
  }, [organization?.id])

  const handleDateRangeChange = (dates) => {
    const [start, end] = dates
    setFromDate(start)
    setToDate(end)
  }

  if (!organization?.id) {
    return (
      <section className="spacer-sm">
        <header className="section-header-sm">
          <div className="section-heading">
            <h1>Deposits</h1>
          </div>
          <p className="section-header-desc">
            Select an organization to view deposits.
          </p>
        </header>
      </section>
    )
  }

  if (loading && deposits.length === 0 && !loadError) {
    return <PageLoadingContainer style="without-sidebar" />
  }

  return (
    <section className="spacer-sm">
      <header className="section-heading--flex section-header-sm">
        <div className="section-heading">
          <h1>Deposits</h1>
          <p className="section-header-desc">
            Deposits recorded for your organization. Create new deposits from{' '}
            <strong>Create</strong> → <strong>Deposit</strong>.
          </p>
        </div>
        <Stack direction="horizontal" gap={2} className="flex-wrap">
          <Button size="lg" type="button" onClick={handleDepositShow}>
            New deposit
          </Button>
        </Stack>
      </header>

      <ReportActions
        isLoading={loading}
        onRefresh={refresh}
        isRefreshing={loading}
        selected={organization?.id}
        fromDate={fromDate}
        toDate={toDate}
        onDateRangeChange={handleDateRangeChange}
      >
        <Form.Group style={{ minWidth: '200px' }}>
          <Form.Label className="small text-muted mb-1 text-nowrap">
            Payment method
          </Form.Label>
          <Form.Select
            size="sm"
            className="form-control-height"
            value={paymentMethodFilter}
            onChange={(e) => setPaymentMethodFilter(e.target.value)}
            disabled={loading}
          >
            <option value="">All methods</option>
            {paymentMethodChoices.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      </ReportActions>

      {loadError && (
        <Card body className="card--md mb-3">
          <p className="text-danger mb-0">{loadError}</p>
        </Card>
      )}

      {deposits.length === 0 && !loadError ? (
        <Card body>
          <EmptyContainer style="center lg discount">
            <p>No deposits yet</p>
            <p className="subtitle subtitle--dark">
              When you record a deposit, it will appear in this list.
            </p>
          </EmptyContainer>
        </Card>
      ) : filteredDeposits.length === 0 && !loadError ? (
        <Card body>
          <EmptyContainer style="center lg discount">
            <p>No deposits match your filters</p>
            <p className="subtitle subtitle--dark">
              Adjust the date range or payment method, or clear filters to see
              all deposits.
            </p>
          </EmptyContainer>
        </Card>
      ) : (
        <Card body className="card--md card--light">
          <Modal
            show={depositNoteModal != null}
            onHide={() => setDepositNoteModal(null)}
            centered
            animation={false}
          >
            <Modal.Header closeButton>
              <Modal.Title>Note</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div
                className="mb-0 text-body text-break"
                style={{ whiteSpace: 'pre-wrap' }}
              >
                {depositNoteModal ?? ''}
              </div>
            </Modal.Body>
          </Modal>

          <div className="table-container">
            <div className="table-container--fixed-height--scrollable">
              <Table responsive>
                <thead>
                  <tr className="md md--scroll">
                    <th className="text-left uppercase text-body">Recorded</th>
                    <th className="text-left uppercase text-body">Name</th>
                    <th className="text-left uppercase text-body">Email</th>
                    <th className="text-left uppercase text-body sm">Phone</th>
                    <th className="text-end uppercase text-body">Amount</th>
                    <th className="text-left uppercase text-body sm">Method</th>
                    <th className="text-left uppercase text-body sm">Option</th>
                    <th className="text-left uppercase text-body lg">
                      Seat info
                    </th>
                    <th className="text-center uppercase text-body sm">Note</th>
                    <th className="text-left uppercase text-body sm">
                      Payment intent
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDeposits.map((row) => {
                    const d = row.attributes ?? row
                    const id = row.id ?? d.id
                    const first = d.firstName ?? ''
                    const last = d.lastName ?? ''
                    const name =
                      capitalizeString(`${first} ${last}`.trim()) || '—'
                    const noteText = depositNoteText(row)
                    return (
                      <tr key={id} className="md-x md-x--scroll">
                        <td className="text-left text-nowrap">
                          {formatRecordedAt(row)}
                        </td>
                        <td className="text-left">{name}</td>
                        <td className="text-left">{d.email ?? '—'}</td>
                        <td className="text-left">
                          {formatPhoneNumber(d.phoneNumber)}
                        </td>
                        <td className="text-end">
                          {formatCurrency(depositListAmount(row))}
                        </td>
                        <td className="text-left">
                          {d.paymentMethod
                            ? formatString(d.paymentMethod)
                            : '—'}
                        </td>
                        <td className="text-left">
                          {d.paymentOption
                            ? formatString(d.paymentOption)
                            : '—'}
                        </td>
                        <td className="text-left small">
                          {formatSeatInfoLines(d.seatInfo)}
                        </td>
                        <td className="text-center align-middle">
                          {noteText ? (
                            <button
                              type="button"
                              className="btn btn-link btn-sm p-1 text-primary border-0"
                              aria-label="View deposit note"
                              title="View note"
                              onClick={() => setDepositNoteModal(noteText)}
                            >
                              <DepositNoteIcon />
                            </button>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                        <td className="text-left small text-muted">
                          {shortenId(d.paymentIntentId)}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </Table>
            </div>
          </div>
        </Card>
      )}
    </section>
  )
}

export default DepositsWrapper
export { DepositsWrapper }
